import { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './PieChart.module.scss';

import PieSlice from './pie-slice/PieSlice';
import DetailPanel from './detail-panel/DetailPanel';
import { rmScoreDecompositionAdaptor, safeSlicing } from './helpers';

const PieChart = ({ chartData, setVisibility }) => {
    if (!chartData) {
        return null;
    }

    const START_ANGLE = -90;
    const MAX_SCORE = 500;

    const [isDetailPanelVisible, setIsDetailPanelVisible] = useState(true);
    const [detailPanelData, setDetailPanelData] = useState();
    const [panelPosition, setPanelPosition] = useState({
        x: 0,
        y: 0
    });

    /**
     * ref: https://stackoverflow.com/questions/73582001/bring-front-of-the-selected-svg-path
     *
     * This will bring the hovering svg slice to the top.
     * This will fix the hiding border issue.
     */
    if (!window.customElements.get('svg-hover')) {
        window.customElements.define(
            'svg-hover',
            class extends HTMLElement {
                connectedCallback() {
                    setTimeout(() => {
                        // make sure <svg> was parsed, thus available in the DOM
                        let svg = this.querySelector('svg');
                        this.querySelectorAll('[hover]').forEach(
                            (el) => (el.onmouseenter = () => svg.append(el))
                        );
                    });
                }
            }
        );
    }

    const calculateAngles = (array) => {
        return array.reduce((p, c, i) => {
            const prev = i === 0 ? {} : p[i - 1];
            const angle =
                ((safeSlicing(prev.score) || safeSlicing(c.score)) * 360) /
                MAX_SCORE;

            return [
                ...p,
                { ...c, start: i === 0 ? START_ANGLE : prev.start + angle }
            ];
        }, []);
    };

    const onMouseMove = (event) => {
        const nEvent = event.nativeEvent;
        const { layerX, layerY } = nEvent;

        setPanelPosition({
            x: layerX + 20,
            y: layerY + 20
        });
    };

    const onSliceHover = (data) => {
        setIsDetailPanelVisible(!!data);
        setDetailPanelData(data);
    };

    const generateDetailPanel = (data) => {
        if (!data) {
            return null;
        }
        return <DetailPanel position={panelPosition} data={data} />;
    };

    const getUserColor = () => {
        let color;

        if (chartData.totalScore <= chartData.moderateThreshold) {
            color = '#15C988'; // green;
        } else if (
            chartData.totalScore > chartData.moderateThreshold &&
            chartData.totalScore < chartData.majorThreshold
        ) {
            color = '#FFB748'; // yellow
        } else if (chartData.totalScore >= chartData.majorThreshold) {
            color = '#EB4560'; // red
        }

        return color;
    };

    const onUserClick = () => {
        setVisibility(true);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.chartTitle}>RM Risk Profile</div>
            {isDetailPanelVisible && generateDetailPanel(detailPanelData)}
            <div className={styles.svgWrapper}>
                <svg-hover>
                    <svg
                        version="1.1"
                        id="rm_pie_chart"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 200 200"
                        className={styles.svg}
                    >
                        {calculateAngles(
                            rmScoreDecompositionAdaptor(chartData).filter(
                                (s) => s.score > 0
                            )
                        ).map((s) => (
                            <PieSlice
                                data={s}
                                key={s.name}
                                fill={s.fill}
                                score={s.score}
                                startAngle={s.start}
                                onMouseMove={onMouseMove}
                                onSliceHover={onSliceHover}
                                isHovering={
                                    detailPanelData
                                        ? s.name === detailPanelData.name
                                        : null
                                }
                            />
                        ))}

                        <symbol
                            id="user-image"
                            viewBox="0 0 24 24"
                            width={24}
                            height={24}
                        >
                            <g>
                                <path
                                    d="M12 15.375C14.2782 15.375 16.125 13.5282 16.125 11.25C16.125 8.97183 14.2782 7.125 12 7.125C9.72183 7.125 7.875 8.97183 7.875 11.25C7.875 13.5282 9.72183 15.375 12 15.375Z"
                                    fill={getUserColor()}
                                />
                                <path
                                    d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7471 9.41504 20.7189 6.93679 18.8911 5.10894C17.0632 3.28109 14.585 2.25293 12 2.25ZM18.1648 17.476C17.5628 16.6037 16.782 15.8695 15.8744 15.3221C14.8308 16.3188 13.4432 16.875 12 16.875C10.5569 16.875 9.16923 16.3188 8.12558 15.3221C7.218 15.8695 6.43724 16.6037 5.83516 17.476C4.77814 16.2874 4.08725 14.8186 3.84568 13.2464C3.60411 11.6743 3.82217 10.0658 4.4736 8.6147C5.12502 7.16361 6.18204 5.93177 7.51737 5.06752C8.85269 4.20327 10.4094 3.74346 12 3.74346C13.5906 3.74346 15.1473 4.20327 16.4826 5.06752C17.8179 5.93177 18.875 7.16361 19.5264 8.6147C20.1778 10.0658 20.3959 11.6743 20.1543 13.2464C19.9127 14.8186 19.2218 16.2874 18.1648 17.476Z"
                                    fill={getUserColor()}
                                />
                            </g>
                        </symbol>

                        <use
                            href="#user-image"
                            x={100 - 12}
                            y={100 - 12}
                            className={styles.userImage}
                            onClick={onUserClick}
                        />
                    </svg>
                </svg-hover>
            </div>
            <div className={styles.totalScore}>{chartData.totalScore}</div>
            <div className={styles.footer}>Total Risk Score</div>
        </div>
    );
};

PieChart.propTypes = {
    chartData: PropTypes.shape({
        totalScore: PropTypes.number,
        moderateThreshold: PropTypes.number,
        majorThreshold: PropTypes.number
    }),
    setVisibility: PropTypes.func
};
export default PieChart;
